import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import './ForgotPassword.css';
import { supabase, isSupabaseConfigured } from '../lib/supabase';

function ForgotPassword() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    password: '',
    confirmPassword: ''
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [isValidToken, setIsValidToken] = useState(false);

  useEffect(() => {
    const checkToken = async () => {
      const hashParams = new URLSearchParams(window.location.hash.substring(1));
      const accessToken = hashParams.get('access_token');
      const type = hashParams.get('type');
      
      if (accessToken && type === 'recovery') {
        try {
          const { data, error: sessionError } = await supabase.auth.setSession({
            access_token: accessToken,
            refresh_token: hashParams.get('refresh_token')
          });
          
          if (!sessionError && data?.session) {
            setIsValidToken(true);
          } else {
            setError('Invalid or expired reset link. Please request a new password reset.');
          }
        } catch (err) {
          setError('Invalid or expired reset link. Please request a new password reset.');
        }
      } else {
        setError('Invalid reset link. Please request a new password reset.');
      }
    };

    if (isSupabaseConfigured) {
      checkToken();
    } else {
      setError('Supabase is not configured. Please contact support.');
    }
  }, [isSupabaseConfigured]);

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    setError('');
    setSuccess('');
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    if (!formData.password || !formData.confirmPassword) {
      setError('Please fill in all fields');
      setLoading(false);
      return;
    }

    if (formData.password.length < 6) {
      setError('Password must be at least 6 characters');
      setLoading(false);
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      setLoading(false);
      return;
    }

    try {
      const { error: updateError } = await supabase.auth.updateUser({
        password: formData.password
      });

      if (updateError) throw updateError;

      setSuccess('Password reset successfully! Redirecting to login...');
      
      setTimeout(async () => {
        await supabase.auth.signOut();
        navigate('/resources');
      }, 2000);
    } catch (error) {
      setError(error.message || 'Failed to reset password. Please try again.');
      setLoading(false);
    }
  };

  if (!isSupabaseConfigured) {
    return (
      <div className="forgot-password-container">
        <div className="auth-container">
          <div className="auth-card">
            <div className="auth-header">
              <h1 className="auth-title">Configuration Required</h1>
              <p className="auth-subtitle">Supabase credentials are not configured</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="forgot-password-container">
      <div className="auth-container">
        <div className="auth-card">
          <div className="auth-header">
            <h1 className="auth-title">Reset Password</h1>
            <p className="auth-subtitle">Enter your new password</p>
          </div>

          {!isValidToken && !error && (
            <div className="auth-loading">
              <div className="auth-loading-spinner"></div>
              <p>Verifying reset link...</p>
            </div>
          )}

          {isValidToken && (
            <form className="auth-form" onSubmit={handleResetPassword}>
              <div className="auth-form-group">
                <label htmlFor="password" className="auth-label">New Password</label>
                <input
                  type="password"
                  id="password"
                  name="password"
                  className="auth-input"
                  placeholder="Enter your new password"
                  value={formData.password}
                  onChange={handleInputChange}
                  required
                  minLength={6}
                />
              </div>

              <div className="auth-form-group">
                <label htmlFor="confirmPassword" className="auth-label">Confirm Password</label>
                <input
                  type="password"
                  id="confirmPassword"
                  name="confirmPassword"
                  className="auth-input"
                  placeholder="Confirm your new password"
                  value={formData.confirmPassword}
                  onChange={handleInputChange}
                  required
                  minLength={6}
                />
              </div>

              {error && <div className="auth-error">{error}</div>}
              {success && <div className="auth-success">{success}</div>}

              <button 
                type="submit" 
                className="auth-submit-btn"
                disabled={loading}
              >
                {loading ? 'Resetting Password...' : 'Reset Password'}
              </button>
            </form>
          )}

          {error && !isValidToken && (
            <div className="auth-error-container">
              <div className="auth-error">{error}</div>
              <button
                className="auth-submit-btn"
                onClick={() => navigate('/resources')}
              >
                Back to Login
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default ForgotPassword;
